import React, { useState } from 'react';
import { Star, ArrowRight } from 'lucide-react';
type Testimonial = {
  name: string;
  city: string;
  title: string;
  text: string;
  before: string;
  after: string;
  time: string;
  img?: string;
  isUserSubmitted?: boolean;
};
const initialTestimonials: Testimonial[] = [{
  title: "I felt like I was 'allergic' to good news.",
  name: 'Amanda S.',
  city: 'Seattle, WA',
  text: "For three months, everything I touched fell apart. My car's transmission died, my landlord raised the rent, and I was passed over for a promotion I was promised. I told my sister I felt like I had a 'black cloud' over my head. I bought the 417hz track at 2 AM on a Tuesday because I was crying and desperate. By Day 4 of listening, the 'heavy' feeling in my chest just... evaporated. The next day, a headhunter messaged me on LinkedIn for a role that pays 20% more. This isn't just audio; it's a life-reset.",
  before: 'Constant bad luck streak',
  after: 'New job offer & peace',
  time: '5 Days',
  img: 'https://i.pravatar.cc/150?img=32'
}, {
  title: 'I stopped attracting the wrong energy.',
  name: 'Jessica R.',
  city: 'Miami, FL',
  text: "I've spent thousands on crystals and 'cleansing' retreats, but I kept attracting the same toxic patterns in my life. Serena's approach was different—she talked about 'static interference.' I realized my frequency was literally tuned to the wrong station. I started the 7-day protocol, and the shift in my household was immediate. Even my kids seemed calmer. Two weeks later, I met someone who is actually consistent and kind. I finally feel 'visible' to the right people.",
  before: 'Toxic attraction patterns',
  after: 'High-value relationship & home peace',
  time: '12 Days',
  img: 'https://i.pravatar.cc/150?img=28'
}, {
  title: "I'm a data analyst. I don't believe in 'manifesting.' But this...",
  name: 'Diana L.',
  city: 'Austin, TX',
  text: "I'm an engineer, so I need logic. When Serena explained the 417hz frequency in terms of sound therapy and nervous system regulation, it clicked. I was going through a brutal legal battle and felt stuck. I used the 'Quick Start Card' instructions and listened every night. On Day 6, the other party called with a settlement offer that was exactly what I wanted. I can't explain it with a spreadsheet, but the frequency works. Period.",
  before: 'Stagnant legal battle',
  after: 'Favorable settlement',
  time: '6 Days',
  img: 'https://i.pravatar.cc/150?img=12'
}, {
  title: 'The 55-second bonus saved my sanity.',
  name: 'Maria G.',
  city: 'San Diego, CA',
  text: "The main audio is great, but the 'Aftershock Reset' bonus is my secret weapon. I used to get post-argument anxiety that would ruin my whole day. Last week, after a huge blow-up with my boss, I used the 55-second sequence in the bathroom. My heart rate dropped instantly, and the 'doom-loop' in my head stopped. I walked back in with so much clarity that he actually apologized to ME. It feels like having a remote control for my luck.",
  before: 'Chronic post-conflict anxiety',
  after: 'Instant emotional control',
  time: '55 Seconds',
  img: 'https://i.pravatar.cc/150?img=44'
}, {
  title: "Finally, the 'static' is gone.",
  name: 'Sarah P.',
  city: 'New York, NY',
  text: "I'm a freelancer, and I had been in a 'dry spell' for 2 months. Every lead went cold. I realized my energy was screams 'desperation,' and it was blocking everything. I did the Lucky Flush protocol and focused on the 'Energy Leak Finder' guide. I found a massive leak in my digital environment. I cleared it, listened to the track, and within 48 hours, a client I hadn't heard from in a year reached out with a $3,500 project. The static is gone, and the flow is back.",
  before: '2-month financial dry spell',
  after: '$3,500 unexpected project',
  time: '48 Hours',
  img: 'https://i.pravatar.cc/150?img=31'
}];
export function TestimonialCarousel() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>(initialTestimonials);
  // Form state
  const [reviewName, setReviewName] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const handleSubmitReview = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reviewName.trim() || !reviewText.trim()) return;
    const newReview: Testimonial = {
      name: reviewName.trim(),
      city: 'Verified Buyer',
      title: 'My Experience',
      text: reviewText.trim(),
      before: 'Interference',
      after: 'Clarity',
      time: 'Recently',
      isUserSubmitted: true
    };
    setTestimonials([newReview, ...testimonials]);
    setReviewName('');
    setReviewText('');
    setReviewRating(5);
  };
  const renderStars = (rating: number) => {
    return <div className="flex space-x-1 mb-3" data-id="element-255">
        {[1, 2, 3, 4, 5].map(star => {
        if (rating >= star) {
          return <Star key={star} className="w-4 h-4 fill-gold text-gold" data-id="element-256" />;
        } else if (rating >= star - 0.5) {
          return <div key={star} className="relative w-4 h-4" data-id="element-257">
                <Star className="absolute inset-0 w-4 h-4 text-gold/30" data-id="element-258" />
                <div className="absolute inset-0 overflow-hidden w-[50%]" data-id="element-259">
                  <Star className="w-4 h-4 fill-gold text-gold" data-id="element-260" />
                </div>
              </div>;
        } else {
          return <Star key={star} className="w-4 h-4 text-gold/30 fill-transparent" data-id="element-261" />;
        }
      })}
      </div>;
  };
  const getInitialColor = (name: string) => {
    const colors = ['bg-rose', 'bg-gold', 'bg-burgundy', 'bg-blue-600', 'bg-emerald-600', 'bg-purple-600'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };
  return <section className="w-full py-24 bg-navy overflow-hidden" data-id="element-262">
      <div className="max-w-5xl mx-auto px-4 flex flex-col items-center" data-id="element-263">
        <p className="text-gold text-sm tracking-[0.2em] uppercase mb-6 font-medium text-center" data-id="element-264">
          WHAT OTHERS ARE SAYING
        </p>

        <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-cream text-center mb-16" data-id="element-265">
          Real Shifts. Real Results.
        </h2>

        {/* Static Testimonials Grid/List */}
        <div className="w-full flex flex-col space-y-8 mb-24" data-id="element-266">
          {testimonials.map((t, idx) => <div key={idx} className="w-full bg-navy-dark border border-gold/30 rounded-2xl p-6 md:p-10 shadow-xl flex flex-col md:flex-row gap-6 md:gap-10" data-id="element-267">
              {/* Left Column: User Info */}
              <div className="flex flex-col items-start md:w-1/4 flex-shrink-0" data-id="element-268">
                <div className="flex items-center space-x-4 md:space-x-0 md:flex-col md:items-start mb-4 md:mb-0" data-id="element-269">
                  {t.isUserSubmitted ? <div className={`w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center text-white font-serif text-2xl md:mb-4 ${getInitialColor(t.name)}`} data-id="element-270">
                      {t.name.charAt(0).toUpperCase()}
                    </div> : <img src={t.img} alt={t.name} className="w-14 h-14 md:w-16 md:h-16 rounded-full object-cover border-2 border-gold/30 md:mb-4" data-id="element-271" />}
                  <div data-id="element-272">
                    <p className="text-gold font-bold text-lg" data-id="element-273">{t.name}</p>
                    <p className="text-cream/50 text-sm" data-id="element-274">{t.city}</p>
                  </div>
                </div>
              </div>

              {/* Right Column: Review Content */}
              <div className="flex-1 flex flex-col" data-id="element-275">
                {renderStars(5)}
                <h3 className="font-serif text-xl md:text-2xl text-cream mb-4" data-id="element-276">
                  "{t.title}"
                </h3>
                <p className="text-cream/80 italic leading-relaxed mb-8 text-lg" data-id="element-277">
                  "{t.text}"
                </p>

                {/* Before/After Box */}
                <div className="bg-navy border border-white/5 rounded-xl p-4 md:p-5 mt-auto" data-id="element-278">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-2 text-sm" data-id="element-279">
                    <div className="flex flex-col" data-id="element-280">
                      <span className="text-cream/40 uppercase tracking-wider text-[10px] font-bold mb-1" data-id="element-281">
                        Before
                      </span>
                      <span className="text-rose font-medium" data-id="element-282">{t.before}</span>
                    </div>
                    <div className="hidden md:flex items-center justify-center" data-id="element-283">
                      <ArrowRight className="w-5 h-5 text-gold/30" data-id="element-284" />
                    </div>
                    <div className="flex flex-col" data-id="element-285">
                      <span className="text-cream/40 uppercase tracking-wider text-[10px] font-bold mb-1" data-id="element-286">
                        After
                      </span>
                      <span className="text-gold font-medium" data-id="element-287">{t.after}</span>
                    </div>
                    <div className="flex flex-col md:border-l md:border-white/10 md:pl-4 mt-2 md:mt-0 pt-2 md:pt-0 border-t border-white/10 md:border-t-0" data-id="element-288">
                      <span className="text-cream/40 uppercase tracking-wider text-[10px] font-bold mb-1" data-id="element-289">
                        Time to Result
                      </span>
                      <span className="text-cream font-bold" data-id="element-290">{t.time}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>)}
        </div>

        {/* Review Submission Form */}
        <div className="w-full max-w-2xl bg-navy-dark border border-gold/20 rounded-2xl p-8 md:p-10 shadow-2xl" data-id="element-291">
          <h3 className="font-serif text-2xl md:text-3xl text-cream text-center mb-8" data-id="element-292">
            Share Your Experience
          </h3>

          <form onSubmit={handleSubmitReview} className="flex flex-col space-y-6" data-id="element-293">
            <div data-id="element-294">
              <label htmlFor="name" className="block text-sm font-medium text-cream/70 mb-2" data-id="element-295">
                Your Name
              </label>
              <input type="text" id="name" value={reviewName} onChange={e => setReviewName(e.target.value)} placeholder="Your name" required className="w-full bg-navy border border-gold/30 rounded-lg px-4 py-3 text-cream placeholder-cream/30 focus:outline-none focus:border-gold transition-colors" data-id="element-296" />
            </div>

            <div data-id="element-297">
              <label className="block text-sm font-medium text-cream/70 mb-2" data-id="element-298">
                Your Rating
              </label>
              <div className="flex space-x-2" data-id="element-299">
                {[1, 2, 3, 4, 5].map(star => <button key={star} type="button" onClick={() => setReviewRating(star)} onMouseEnter={() => setHoverRating(star)} onMouseLeave={() => setHoverRating(0)} className="focus:outline-none" data-id="element-300">
                    <Star className={`w-8 h-8 transition-colors ${(hoverRating || reviewRating) >= star ? 'fill-gold text-gold' : 'text-gold/30 fill-transparent'}`} data-id="element-301" />
                  </button>)}
              </div>
            </div>

            <div data-id="element-302">
              <label htmlFor="review" className="block text-sm font-medium text-cream/70 mb-2" data-id="element-303">
                Your Review
              </label>
              <textarea id="review" value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder="Tell us about your experience..." required rows={4} className="w-full bg-navy border border-gold/30 rounded-lg px-4 py-3 text-cream placeholder-cream/30 focus:outline-none focus:border-gold transition-colors resize-none" data-id="element-304" />
            </div>

            <button type="submit" className="w-full py-4 bg-cta-red text-white font-bold text-lg rounded-full animate-heartbeat hover:bg-red-700 transition-colors duration-300 mt-4" data-id="element-305">
              Submit My Review
            </button>
          </form>
        </div>
      </div>
    </section>;
}